import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8868DueDate = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8868 Due Dates: File Extension Timely & Avoid Penalties"
          description="Discover the critical due dates for Form 8868 and ensure a seamless filing process. File on time to secure a 6-month extension for your nonprofit organization."
          keywords=""
          canonical="resources/tax-extension/form-8868-due-dates/"
          ogType="article"
          jsonLdPricing="14.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 8868</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Form 8868 before the IRS deadline to request an automatic
          extension and{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          remain fully compliant with federal filing requirements.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868duedates`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-8868-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8868 due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>Form 8868</span> is
                        used by{' '}
                        <span style={{ fontWeight: 600 }}>
                          tax-exempt organizations
                        </span>{' '}
                        to request an automatic extension of time to file
                        eligible exempt organization returns.
                      </span>
                    </li>
                    <li>
                      <span>
                        For organizations following a{' '}
                        <span style={{ fontWeight: 600 }}>
                          calendar tax year ending December 31
                        </span>
                        , Form 8868 is generally due by{' '}
                        <span style={{ fontWeight: 600 }}>May 15</span> of the
                        following year.
                      </span>
                    </li>
                    <li>
                      <span>
                        For organizations using a{' '}
                        <span style={{ fontWeight: 600 }}>fiscal tax year</span>
                        , Form 8868 is generally due by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          15th day of the 5th month
                        </span>{' '}
                        after the end of the organization's tax year.
                      </span>
                    </li>
                    <li>
                      <span>
                        <a
                        // href="https://www.taxzerone.com/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                        // style={{
                        //   color: '#0202ff',
                        //   textDecoration: 'underline',
                        // }}
                        >
                          Filing Form 8868
                        </a>{' '}
                        by the original due date generally provides an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic 6-month extension
                        </span>{' '}
                        to file the applicable exempt organization return.
                      </span>
                    </li>
                    <li>
                      <span>
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          E-filing Form 8868
                        </a>{' '}
                        can help organizations submit their extension request
                        electronically and receive confirmation of the filing.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 4 }}>
                    Tax season can feel like a cumbersome task, especially for
                    exempt organizations and employers filing complex forms like
                    990. But don’t worry. Form 8868 is your safety net, which
                    grants you a six-month extension to gather documents and
                    avoid the late-filing penalties.
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                {/* </Stack> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 8868?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Charities, nonprofit groups, and tax-exempt organizations can
                  use{' '}
                  <Link
                    href="/resources/tax-extension/form-8868-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8868
                  </Link>{' '}
                  to request an automatic 6-month extension of time to file
                  their information returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These organizations do not need to provide a reason when{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 8868
                  </Link>{' '}
                  for the extension. Simply submitting the form by the original
                  due date will automatically provide up to a 6-month filing
                  extension.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 8868, Application for Automatic Extension of Time To
                  File an Exempt Organization Return, is the specific IRS form
                  used by tax-exempt groups to obtain this automatic extension.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> The extension only applies to the filing of the
                  return, not to the payment of any taxes owed.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the deadline for filing Form 8868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The due date for filing Form 8868 depends on the end date of
                  the tax-exempt organization&apos;s accounting period.
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For organizations following the calendar tax year (ending
                      December 31), Form 8868 is due on <b>May 15</b>.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For organizations following a fiscal tax year, Form 8868
                      must be filed by the{' '}
                      <span style={{ fontWeight: 600 }}>
                        15th day of the 5th month
                      </span>{' '}
                      after their tax year ends.
                    </ListItemText>
                  </ListItem>
                </List>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Don&apos;t wait till the deadline!
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 8868 now and secure your 6-month extra time
                        to file information returns.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 220, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868duedates`}
                      >
                        E-File Form 8868 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the due date falls on a weekend or federal
                  holiday, the deadline to file Form 8868 is extended to the
                  next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 8868 for the 2025 tax year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  There are two ways to file Form 8868 with the IRS:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    E-filing Form 8868
                  </Link>{' '}
                  through an authorized e-file provider is the quickest and
                  easiest way to file for an extension. The benefits of e-filing
                  include fast processing, convenience, and accuracy.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  TaxZerone is an IRS-authorized e-file service provider through
                  which you can file Form 8868 extension. You only need to enter
                  the required information, calculate the balance due, and
                  transmit the return to the IRS.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can also file Form 8868 by printing out the form and
                  mailing it to the IRS. If you choose paper filing, make sure
                  to mail the form early and double check for errors.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The irs 990 extension recommends e-filing over paper filing
                  because of its speed, ease of use, and minimal errors.
                  However, paper filing is an option if you cannot e-file for
                  any reason. The key with either method is filing Form 8868
                  before the tax filing deadline.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 8868 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }} component="div">
                  As an IRS-authorized e-file provider, TaxZerone offers a
                  simple and hassle-free way to file{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 8868
                  </Link>
                  .
                  <ol>
                    <li style={{ marginBottom: 16 }}>
                      Enter your organization details, select the tax year, and
                      choose the appropriate IRS form for which you’re applying
                      for an extension.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Calculate the balance due by entering tentative total tax
                      and total payments and credits.
                    </li>
                    <li>
                      Review and transmit the return to the IRS and get notified
                      as soon as the IRS processes your return.
                    </li>
                  </ol>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Looks simple, right? Start your Form 8868 filing
              <br /> with TaxZerone now
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868duedates`}
              >
                E-file Form 8868
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get your 6-month extension period
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8868DueDate;
